import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useRBAC } from '@/contexts/RBACContext';
import { useNotificationSound } from '@/contexts/NotificationSoundContext';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Phone, Share2, TrendingUp, LogOut, Sparkles, 
  Target, Settings, Rocket, Megaphone, Database, Building, Workflow, 
  CalendarCheck, Plug, UsersRound, Building2, Landmark, Shuffle,
  ChevronDown, ChevronRight, BarChart3, Briefcase, MessageSquare, Shield, Inbox,
  PanelLeftClose, PanelLeft, User, Bell, UserPlus, Check, X, Calendar, CheckSquare, Mail,
  FolderOpen, Menu, Layers, Clock, Brain, PhoneCall, Globe, Activity, Settings2, GitBranch, FileText,
  ClipboardList, Search, Mic, Zap, GraduationCap, BookOpen, Award, Command, Volume2, VolumeX, Crown, Trophy, Handshake, Car
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

import AIChatWidget from '@/components/AIChatWidget';
import FollowupReminderOverlay from '@/components/FollowupReminderOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Navigation Category Component
const NavCategory = ({ label, icon: Icon, items, defaultOpen = false, collapsed = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const location = useLocation();
  
  const hasActiveChild = items.some(item => location.pathname === item.path);
  const isExpanded = isOpen || hasActiveChild;
  
  if (collapsed) {
    return (
      <div className="mb-1">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`flex items-center justify-center py-2.5 mx-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  hasActiveChild 
                    ? 'text-white bg-white/10' 
                    : 'text-white/50 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={18} className={hasActiveChild ? 'text-accent' : ''} />
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-popover border-border shadow-elevation-3">
              <div className="space-y-1">
                <p className="font-medium text-foreground">{label}</p>
                {items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block text-sm py-1 transition-colors ${
                      location.pathname === item.path ? 'text-accent' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }
  
  return (
    <div className="mb-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-200 rounded-lg mx-2 ${
          hasActiveChild 
            ? 'text-white bg-white/10' 
            : 'text-white/60 hover:text-white hover:bg-white/5'
        }`}
        style={{ width: 'calc(100% - 16px)' }}
      >
        <Icon size={18} className={hasActiveChild ? 'text-accent' : ''} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown 
          size={16} 
          className={`text-white/40 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {/*
        Expanded submenu container. max-h had to grow: max-h-96 (24rem/384px)
        clipped the bottom items of tall categories like Administration
        (12 submenus × ~36px = ~432px > 384px) — users saw only
        "Lifecycle Stages / Custom Fields & Tags" and a gap before Logout.
        max-h-[1500px] is well above any realistic submenu count, and the
        parent sidebar-nav already has overflow-y-auto so oversize sections
        become scrollable at the sidebar level, not clipped. The max-h
        stays instead of `h-auto` so the height transition still animates.
      */}
      <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="ml-4 mt-1 space-y-0.5">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2 text-sm transition-all duration-200 rounded-lg mx-2 relative ${
                  isActive 
                    ? 'text-white bg-accent/20' 
                    : 'text-white/50 hover:text-white hover:bg-white/5'
                }`}
                style={{ width: 'calc(100% - 16px)' }}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r-full" />
                )}
                <ItemIcon size={16} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Single Navigation Item (for non-categorized items)
const NavItem = ({ path, label, icon: Icon, collapsed = false }) => {
  const location = useLocation();
  const isActive = location.pathname === path;
  
  if (collapsed) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={path}
              className={`flex items-center justify-center py-2.5 rounded-lg mx-2 mb-1 transition-all duration-200 ${
                isActive 
                  ? 'text-white bg-accent/20' 
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon size={18} className={isActive ? 'text-accent' : ''} />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-popover border-border">
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return (
    <Link
      to={path}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all duration-200 rounded-lg mx-2 mb-1 relative ${
        isActive 
          ? 'text-white bg-accent/20' 
          : 'text-white/50 hover:text-white hover:bg-white/5'
      }`}
      style={{ width: 'calc(100% - 16px)' }}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-accent rounded-r-full" />
      )}
      <Icon size={18} className={isActive ? 'text-accent' : ''} />
      <span>{label}</span>
    </Link>
  );
};

// Icon mapping for dynamic navigation
const iconMap = {
  'layout-dashboard': LayoutDashboard,
  'crown': Crown,
  'users': Users,
  'users-round': UsersRound,
  'shuffle': Shuffle,
  'building': Building,
  'folder-open': FolderOpen,
  'landmark': Landmark,
  'building-2': Building2,
  'calendar-check': CalendarCheck,
  'sparkles': Sparkles,
  'bar-chart-3': BarChart3,
  'share-2': Share2,
  'megaphone': Megaphone,
  'inbox': Inbox,
  'message-square': MessageSquare,
  'mail': Mail,
  'settings': Settings,
  'phone': Phone,
  'workflow': Workflow,
  'target': Target,
  'database': Database,
  'plug': Plug,
  'shield': Shield,
  'menu': Menu,
  'layers': Layers,
  'user': User,
  'file': FolderOpen,
  'folder': FolderOpen,
  'globe': Globe,
  'activity': Activity,
  'clipboard-list': ClipboardList,
  'calendar': Calendar,
  'check-square': CheckSquare,
  'search': Search,
  'zap': Zap,
  'clock': Clock,
  'brain': Brain,
  'mic': Mic,
  'briefcase': Briefcase,
  'graduation-cap': GraduationCap,
  'book-open': BookOpen,
  'award': Award,
  'trophy': Trophy,
  'handshake': Handshake,
  'car': Car,
};

const getIconComponent = (iconName) => {
  return iconMap[iconName] || FolderOpen;
};

// Role-scoped sidebar filter. BDM users (Fakhruddin Properties — added
// scopeForRole used to hardcode BDM users to a single path
// (/leads-dashboard/brokerage-hub) regardless of what the admin
// granted. That broke the RBAC contract — an admin couldn't hand a
// BDM extra access via Permission Groups because this filter would
// strip it out on the frontend anyway. Removed 2026-07-06: the
// backend nav (from /api/me/navigation) is now the single source of
// truth for every role. If a role needs to be narrower, tighten its
// permission group in Admin → Permission Groups.
function scopeForRole(menuCategories /* , user */) {
  return menuCategories;
}

export const Sidebar = ({ collapsed, onToggleCollapse, mobileOpen = false, onMobileClose }) => {
  const { logout, user, token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigation, loading: rbacLoading, refreshNavigation } = useRBAC();
  
  // Close mobile menu on navigation
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const categories = useMemo(() => {
    if (!navigation || navigation.length === 0) {
      return [];
    }

    // Paths hidden from the sidebar at the frontend layer (DB rows kept
    // as-is). Kept here so the change stays local without a DB write.
    //   - /organization (Organization Structure): superseded by
    //     Organization Dashboard's embedded Teams panel + the standalone
    //     Team Management page. Redundant nav entry.
    const HIDDEN_PATHS = new Set(['/organization']);

    // Frontend-only per-item ordering override. Values replace the DB's
    // display_order for the given route in a single sort pass — so an
    // override of 6.5 slots the item between DB rows ordered 6 and 7,
    // rather than jumping to the top like a separate "pinned" bucket
    // would. Kept here so the change stays local, no DB write.
    //
    // Current overrides (Administration section, DB base orders in
    // parentheses):
    //   Organizations (8) -> 0   pinned to the top
    //   Team Management (2) -> 1 just below Organizations
    //   Module Management (12) -> 6.5  between Permission Groups (6)
    //                                  and AI Assignment (7)
    const PATH_ORDER_OVERRIDES = {
      '/organizations': 0,
      '/team': 1,
      '/admin/org-modules': 6.5,
    };

    return navigation.map((menu, index) => ({
      id: menu.menu_code.toLowerCase(),
      label: menu.menu_name,
      icon: getIconComponent(menu.icon),
      defaultOpen: index === 0,
      items: (menu.sub_menus || [])
        .filter(submenu => !HIDDEN_PATHS.has(submenu.route_path))
        .slice() // don't mutate the RBAC-context array
        .sort((a, b) => {
          const ao = PATH_ORDER_OVERRIDES[a.route_path] ?? (a.display_order ?? 0);
          const bo = PATH_ORDER_OVERRIDES[b.route_path] ?? (b.display_order ?? 0);
          return ao - bo;
        })
        .map(submenu => ({
          path: submenu.route_path,
          label: submenu.sub_menu_name,
          icon: getIconComponent(submenu.icon),
          permissionCode: submenu.sub_menu_code,
          allowedActions: submenu.allowed_actions || [],
        }))
    })).filter(category => category.items.length > 0);
  }, [navigation]);

  const enhancedCategories = useMemo(() => {
    // Leads Dashboard is now DB-driven — the parent + 5 submenus live in
    // the `menus` / `sub_menus` collections (menu_code=LEAD_DASHBOARD).
    // See scripts/migrate_leads_dashboard.py for the seed. The old static
    // block used to be here; it was removed 2026-07-02 because it caused
    // a duplicate "Leads Dashboard" alongside the DB-driven "Lead
    // Dashboard" that an admin created via Menu Management. Single source
    // of truth = DB.

    // The Sales Competition Dashboard page exists and is routed at
    // /competition, but the RBAC seed has no menu entry for it — so it
    // was unreachable from the sidebar. Inject it here the same way as
    // the Leads Dashboard above. `singleItem: true` tells the render
    // loop to draw it as a one-click NavItem instead of a collapsible
    // category (it's a single page, so a category with one child would
    // need a pointless extra click to expand).
    const COMPETITION = {
      id: 'competition',
      label: 'Competition',
      icon: getIconComponent('trophy'),
      singleItem: true,
      items: [
        {
          path: '/competition',
          label: 'Competition',
          icon: getIconComponent('trophy'),
          permissionCode: 'competition.view',
          allowedActions: ['view'],
        },
      ],
    };

    // Approvals — generic request → approve/reject inbox. Visible
    // to everyone (agents file requests, managers act on them);
    // the page itself scopes the Inbox tab to the user's eligible
    // role set and shows an empty state for non-approvers.
    const APPROVALS = {
      id: 'approvals',
      label: 'Approvals',
      icon: getIconComponent('shield-check'),
      singleItem: true,
      items: [
        {
          path: '/approvals',
          label: 'Approvals',
          icon: getIconComponent('shield-check'),
          permissionCode: 'leads.direct',  // every authenticated user has this
          allowedActions: ['view'],
        },
      ],
    };

    // Car Requests — receptionist + manager queue. Rendered as a single
    // nav item; all logged-in users see it (the page itself scopes by
    // role: agents see only their own requests, receptionists/admins
    // see the whole org).
    const CAR_REQUESTS = {
      id: 'car-requests',
      label: 'Car Requests',
      icon: getIconComponent('car'),
      singleItem: true,
      items: [
        {
          path: '/car-requests',
          label: 'Car Requests',
          icon: getIconComponent('car'),
          permissionCode: 'car-requests.view',
          allowedActions: ['view'],
        },
      ],
    };

    // AI Reports — Mid-Day + EOD reports with 3 read levels. CEO scans
    // in 10s (summary + tiles + high flags), HOD reads in 30s (full
    // dashboard), HR drills into proof records. Page detects role and
    // renders the right depth — single nav item for everyone.
    const AI_REPORTS = {
      id: 'ai-reports',
      label: 'AI Reports',
      icon: getIconComponent('sparkles'),
      singleItem: true,
      items: [
        {
          path: '/ai-reports',
          label: 'AI Reports',
          icon: getIconComponent('sparkles'),
          permissionCode: 'leads.direct',
          allowedActions: ['view'],
        },
      ],
    };


    // Marketing Dashboard sidebar entry removed — the same component
    // is now embedded directly inside the HOD Dashboard page when the
    // department/view is Marketing. Avoids two ways to reach the
    // identical view and keeps the sidebar tight.

    // If the RBAC nav hasn't arrived yet (or came back empty),
    // return an empty tree. The empty-sidebar / spinner branch
    // handles it upstream. Previously we injected 4 static extras
    // (AI Reports, Competition, Car Requests, Approvals) here as a
    // "fallback" — that produced the notorious flash-of-all-menus
    // on remote hosts because the user briefly saw 4 items they
    // didn't earn before their real scoped nav rendered.
    if (!categories || categories.length === 0) {
      return [];
    }
    const next = [...categories];
    // Pull LEAD_DASHBOARD out of its natural display_order slot and
    // place it right after the Dashboard section (asked 2026-07-02).
    // If Dashboard isn't in the user's nav (narrow roles like BDM),
    // put it at the very top so it renders before Settings / other
    // menus rather than getting buried at the bottom.
    const dashIdx = next.findIndex(
      (c) => c && (c.id === 'dashboard' || c.menu_code === 'DASHBOARD'),
    );
    const leadDashIdx = next.findIndex(
      (c) => c && (c.id === 'lead_dashboard' || c.menu_code === 'LEAD_DASHBOARD'),
    );
    if (leadDashIdx !== -1) {
      const [leadDash] = next.splice(leadDashIdx, 1);
      // Prepend an "Overview" item as the first sub-menu inside Lead
      // Dashboard so users land on the summary page before diving into
      // a specific area. Static, no DB write. Duplicates are guarded.
      const hasOverview = (leadDash.items || []).some(
        (it) => it.path === '/leads-dashboard',
      );
      const leadDashWithOverview = hasOverview ? leadDash : {
        ...leadDash,
        items: [
          {
            path: '/leads-dashboard',
            label: 'Overview',
            icon: getIconComponent('layout-dashboard'),
            permissionCode: 'LEAD_DASHBOARD.OVERVIEW',
            allowedActions: ['view'],
          },
          ...(leadDash.items || []),
        ],
      };
      // If Dashboard exists, insert LEAD_DASHBOARD directly after it;
      // otherwise render at the top so it's the first thing the user sees.
      const insertAt = dashIdx === -1 ? 0 : (dashIdx < leadDashIdx ? dashIdx + 1 : dashIdx);
      next.splice(insertAt, 0, leadDashWithOverview);
    }
    // Static injections — AI Reports / Competition / Car Requests /
    // Approvals — are only added for roles that already see a broader
    // navigation. If the backend returned fewer than 4 top-level menus,
    // the user is on a narrow role (BDM / brokerage-only / etc.) and
    // adding these would break the "sidebar shows only what the role
    // grants" contract. In that case we return just the DB-driven nav.
    if (categories.length >= 4) {
      next.splice(2, 0, AI_REPORTS);
      next.splice(3, 0, COMPETITION);
      next.splice(4, 0, CAR_REQUESTS);
      next.splice(5, 0, APPROVALS);
    }

    // Inject "User Mapping" as an admin-only sub-item under the
    // ADMINISTRATION category. Only shown when the user already sees
    // that section (real admins). The page itself has an access-gate
    // for role safety.
    const isAdminUser = ['super_admin', 'admin', 'account_owner', 'owner']
      .includes(user?.role);
    const adminIdx = next.findIndex(
      (c) => c && (c.id === 'administration' || c.menu_code === 'ADMINISTRATION'),
    );
    if (isAdminUser && adminIdx !== -1) {
      const admin = next[adminIdx];
      const alreadyThere = (admin.items || []).some(
        (it) => it.path === '/admin/user-mapping',
      );
      if (!alreadyThere) {
        // Insert right below "User Management" so admins find it in the
        // people-management cluster of the sidebar.
        const insertAt = Math.max(
          0,
          (admin.items || []).findIndex((it) => it.path === '/admin/users') + 1,
        );
        const withItem = {
          ...admin,
          items: [
            ...(admin.items || []).slice(0, insertAt),
            {
              path: '/admin/user-mapping',
              label: 'User Mapping',
              icon: getIconComponent('git-branch'),
              permissionCode: 'ADMINISTRATION.USER_MAPPING',
              allowedActions: ['view'],
            },
            ...(admin.items || []).slice(insertAt),
          ],
        };
        next[adminIdx] = withItem;
      }
    }

    return scopeForRole(next, user);
  }, [categories, user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [isSeeding, setIsSeeding] = useState(false);

  // Refresh the sidebar by clearing the backend's RBAC caches (safe:
  // no schema writes) and forcing the frontend to refetch. The old
  // handler here hit /admin/rbac/force-seed, which WIPES and re-seeds
  // every RBAC table — destructive if an admin ever clicks it, and a
  // dead-end 403 for non-admins. This safer version handles both the
  // "stale in-process nav cache" case (backend restart / DB blip) and
  // the "React state fell behind after a role change" case.
  const handleFixNavigation = async () => {
    if (!token) {
      toast.error('Please log in first');
      return;
    }
    setIsSeeding(true);
    try {
      // Best-effort cache clear. Non-admins will 403, but the local
      // refetch below still runs and usually resolves the issue.
      try {
        await axios.post(`${API}/admin/cache/clear?token=${token}`);
      } catch (_) { /* ignore 403/404 — the refetch is the real fix */ }

      // Ask RBACContext to re-hit /me/navigation.
      if (typeof refreshNavigation === 'function') {
        await refreshNavigation();
      } else {
        // Fallback: hard-reload if the context isn't exposing refresh.
        window.location.reload();
        return;
      }
      toast.success('Sidebar refreshed');
    } catch (error) {
      console.error('Fix navigation error:', error);
      toast.error(error.response?.data?.detail || 'Could not refresh the sidebar. Try logging out and back in.');
    } finally {
      setIsSeeding(false);
    }
  };

  // Only admins should see the recovery button. For everyone else the
  // right action is "contact your administrator" — a button that 403s
  // is confusing and destructive-looking.
  const isAdminUser = ['super_admin', 'admin', 'owner', 'account_owner'].includes(
    (user?.role || '').toLowerCase(),
  );
  
  // Loading state
  if (rbacLoading) {
    return (
      <aside className={`sidebar ${collapsed ? 'w-16' : 'w-64'} ${mobileOpen ? 'open' : ''}`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-accent/30 blur-xl rounded-full" />
              <div className="relative w-10 h-10 rounded-xl bg-accent flex items-center justify-center shadow-glow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-semibold text-lg tracking-tight text-white">Treppan</h1>
                <p className="text-xs text-white/50">Enterprise CRM</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-white/20 border-t-accent rounded-full animate-spin" />
        </div>
      </aside>
    );
  }
  
  // Empty state - show fix button
  if (categories.length === 0 && !rbacLoading) {
    return (
      <aside className={`sidebar ${collapsed ? 'w-16' : 'w-64'} ${mobileOpen ? 'open' : ''}`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="absolute inset-0 bg-accent/30 blur-xl rounded-full" />
              <div className="relative w-10 h-10 rounded-xl bg-accent flex items-center justify-center shadow-glow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-semibold text-lg tracking-tight text-white">Treppan</h1>
                <p className="text-xs text-white/50">Enterprise CRM</p>
              </div>
            )}
          </div>
        </div>
        
        {!collapsed && (
          <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
              <Menu className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm text-amber-400 font-medium mb-1">
                {isAdminUser ? 'Navigation menus missing' : 'No menus assigned to your role'}
              </p>
              <p className="text-xs text-white/40">
                {isAdminUser
                  ? 'Refresh the sidebar or check role assignments'
                  : 'Ask an administrator to assign you a role and permission group'}
              </p>
            </div>

            {/* Recovery button — admins only. Uses the safe cache-clear
                endpoint (not force-seed, which wipes RBAC state). */}
            {isAdminUser && (
              <Button
                onClick={handleFixNavigation}
                disabled={isSeeding}
                className="w-full bg-accent hover:bg-accent/90"
                data-testid="fix-menus-btn"
              >
                {isSeeding ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    Refresh Sidebar
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        {collapsed && isAdminUser && (
          <div className="flex-1 flex items-center justify-center">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleFixNavigation}
                    disabled={isSeeding}
                    variant="ghost"
                    size="icon"
                    className="text-amber-400 hover:bg-amber-500/20"
                    data-testid="fix-menus-btn-collapsed"
                  >
                    {isSeeding ? (
                      <div className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                    ) : (
                      <Settings className="w-5 h-5" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Refresh Sidebar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
        
        <div className="p-4 border-t border-white/10">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={`w-full text-white/50 hover:text-white hover:bg-white/5 ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
            data-testid="logout-button-empty"
          >
            <LogOut size={18} className={collapsed ? '' : 'mr-3'} />
            {!collapsed && t('nav.logout', 'Logout')}
          </Button>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`sidebar ${collapsed ? 'w-16' : 'w-64'} ${mobileOpen ? 'open' : ''}`}>
      {/* Mobile close button */}
      {mobileOpen && (
        <button 
          onClick={onMobileClose}
          className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white lg:hidden z-50"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      )}
      {/* Logo with glow effect */}
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-accent/30 blur-xl rounded-full" />
            <div className="relative w-10 h-10 rounded-xl bg-accent flex items-center justify-center shadow-glow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-semibold text-lg tracking-tight text-white">Treppan</h1>
              <p className="text-xs text-white/50">Enterprise CRM</p>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Toggle */}
      <div className="px-2 py-2 border-b border-white/10">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleCollapse}
          className={`w-full text-white/50 hover:text-white hover:bg-white/5 ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
          data-testid="sidebar-toggle-btn"
        >
          {collapsed ? <PanelLeft size={18} /> : <><PanelLeftClose size={18} className="mr-2" /> Collapse</>}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav flex-1 py-3 overflow-y-auto">
        {enhancedCategories.map((category) => (
          category.singleItem ? (
            <NavItem
              key={category.id}
              path={category.items[0].path}
              label={category.items[0].label}
              icon={category.icon}
              collapsed={collapsed}
            />
          ) : (
            <NavCategory
              key={category.id}
              label={category.label}
              icon={category.icon}
              items={category.items}
              defaultOpen={category.defaultOpen}
              collapsed={collapsed}
            />
          )
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        {collapsed ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  size="icon"
                  className="w-full text-white/50 hover:text-white hover:bg-white/5"
                  data-testid="logout-button"
                >
                  <LogOut size={18} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{t('nav.logout', 'Logout')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-white/50 hover:text-white hover:bg-white/5"
            data-testid="logout-button"
          >
            <LogOut size={18} className="mr-3" />
            {t('nav.logout', 'Logout')}
          </Button>
        )}
      </div>
    </aside>
  );
};

export const Topbar = ({ onViewChange, currentView, onMobileMenuToggle }) => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessRoute } = useRBAC();
  // Header notification bell mirrors sidebar access to the Notifications
  // page — if the user isn't allowed to open /settings/notifications, we
  // hide the bell entirely (dropdown, badge, and toast entry point).
  const canSeeNotifications = canAccessRoute('/settings/notifications');
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState({});
  const notificationsRef = useRef(null);

  // Close the notifications dropdown when the user clicks anywhere outside
  // the bell + panel wrapper. Listen on `mousedown` so the close happens
  // before any click handler inside other components fires — this prevents
  // ghost selections under the panel.
  useEffect(() => {
    if (!showNotifications) return;
    const onDocMouseDown = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showNotifications]);
  
  // Use the NotificationSoundContext for notifications with sound
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    soundEnabled,
    toggleSound,
    testSound,
    fetchHistory,
  } = useNotificationSound();

  // Date-filtered history (separate from the live `notifications` feed so
  // applying a filter doesn't silence new notification sounds). When a
  // filter is active we render `historyNotifications`; otherwise the live
  // feed. Loaded on demand each time the user changes the dates.
  const [notifFrom, setNotifFrom] = useState('');
  const [notifTo, setNotifTo] = useState('');
  const [historyNotifications, setHistoryNotifications] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const notifFilterActive = Boolean(notifFrom || notifTo);

  useEffect(() => {
    if (!notifFilterActive) {
      setHistoryNotifications([]);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    fetchHistory({ dateFrom: notifFrom, dateTo: notifTo, limit: 500 })
      .then((rows) => {
        if (!cancelled) setHistoryNotifications(rows);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [notifFrom, notifTo, notifFilterActive, fetchHistory]);

  // Whichever feed we hand to the renderer below.
  const visibleNotifications = notifFilterActive ? historyNotifications : notifications;

  useEffect(() => {
    const fetchPendingUsers = async () => {
      if (!token) return;
      
      const adminRoles = ['super_admin', 'account_owner', 'admin', 'owner'];
      if (!adminRoles.includes(user?.role)) return;
      
      try {
        const response = await axios.get(`${API}/accounts/pending-users?token=${token}`);
        if (response.data.success) {
          setPendingUsers(response.data.pending_users || []);
        }
      } catch (error) {
        console.error('Failed to fetch pending users:', error);
      }
    };
    
    fetchPendingUsers();
    const interval = setInterval(fetchPendingUsers, 60000);
    return () => clearInterval(interval);
  }, [token, user?.role]);

  const handleMarkAsRead = async (notificationId) => {
    await markAsRead(notificationId);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleApproveUser = async (userId) => {
    const role = selectedRoles[userId] || 'sales_rep';
    setLoadingPending(true);
    try {
      const response = await axios.post(`${API}/accounts/users/${userId}/approve?token=${token}&role=${role}`);
      if (response.data.success) {
        toast.success(`User approved as ${role}`);
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    } finally {
      setLoadingPending(false);
    }
  };

  const handleRejectUser = async (userId) => {
    setLoadingPending(true);
    try {
      const response = await axios.post(`${API}/accounts/users/${userId}/reject?token=${token}`);
      if (response.data.success) {
        toast.success('User access request rejected');
        setPendingUsers(prev => prev.filter(u => u.id !== userId));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setLoadingPending(false);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      '/': t('nav.contacts', 'Contacts'),
      '/dashboard': t('nav.dashboard', 'Dashboard'),
      '/leads-dashboard/direct-sales': t('nav.directSales', 'Direct Sales'),
      '/leads-dashboard/indirect-sales': t('nav.indirectSales', 'Indirect Sales'),
      '/contacts': t('nav.contacts', 'Contacts'),
      '/lead-assignment': t('nav.leadAssignment', 'Lead Assignment'),
      '/visits': t('nav.salesVisits', 'Sales Visits'),
      '/real-estate': t('nav.realEstate', 'Real Estate'),
      '/developers': t('nav.developerMaster', 'Developer Master'),
      '/brokers': t('nav.brokerMaster', 'Broker Master'),
      '/brokers/dedup': t('nav.brokerDedup', 'Broker Dedup Workspace'),
      '/lead-lifecycle': t('nav.leadLifecycle', 'Lead Lifecycle'),
      '/approvals': t('nav.approvals', 'Approvals'),
      '/calling': t('nav.calling', 'AI Calling Hub'),
      '/social': t('nav.socialCalendar', 'Social Calendar'),
      '/campaign-creator': t('nav.campaignCreator360', 'AI Campaign Creator'),
      '/campaign-analytics': t('nav.campaignAnalytics', 'Campaign Analytics'),
      '/workflows': t('nav.workflows', 'Workflows'),
      '/hubspot': t('nav.hubspotMigration', 'HubSpot Import'),
      '/forecast': t('nav.forecast', 'Revenue Forecasting'),
      '/team': t('nav.teamManagement', 'Team Management'),
      '/integrations': t('nav.integrations', 'Integrations'),
      '/settings': t('nav.settings', 'Settings'),
      '/whatsapp': 'WhatsApp Inbox',
      '/whatsapp-settings': 'WhatsApp Settings',
      '/admin/whatsapp-automation': 'WhatsApp Automation Hub',
      '/admin/ai-call-scheduling': 'AI Call Scheduling',
      '/reporting': 'Reporting Dashboard',
      '/ai-reports': 'AI Reports',
      '/marketing-dashboard': 'Marketing Dashboard',
      '/marketing/automation': 'Email Marketing',
      '/marketing/lists': 'Contact Lists',
      '/marketing/segments': 'Segments',
      '/marketing/templates': 'Email Templates',
      '/marketing/campaigns': 'Email Campaigns',
      '/marketing/campaigns/new': 'Create Campaign',
      '/marketing/social': 'Social Media',
      '/marketing/social/calendar': 'Social Calendar',
      '/marketing/social/inbox': 'Social Inbox',
      '/marketing/journeys': 'Marketing Journeys',
      '/marketing/journeys/new': 'Create Journey',
      '/marketing/forms': 'Forms',
      '/marketing/forms/new': 'Create Form',
      '/profile': t('nav.profileSettings', 'Profile Settings'),
    };
    return titles[path] || 'Treppan CRM';
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isAdmin = ['super_admin', 'account_owner', 'admin', 'owner'].includes(user?.role);
  const totalNotifications = unreadCount + pendingUsers.length;

  return (
    <header className="topbar" data-testid="topbar">
      <div className="flex items-center justify-between w-full">
        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMobileMenuToggle}
            className="mobile-menu-btn flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 text-slate-700"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            style={{ minWidth: '40px', minHeight: '40px' }}
          >
            <Menu size={24} strokeWidth={2} />
          </button>
          {/* Page Title */}
          <h2 className="text-xl font-semibold text-foreground tracking-tight">{getPageTitle()}</h2>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Global Search */}
          <Button 
            variant="outline" 
            size="sm" 
            className="hidden md:flex gap-2 text-muted-foreground border-border/50 hover:border-border bg-background/50"
          >
            <Search size={14} />
            <span>Search</span>
            <kbd className="ml-2 px-1.5 py-0.5 text-[10px] bg-muted rounded text-muted-foreground">⌘K</kbd>
          </Button>
          
          {/* Notification Bell — hidden entirely when the user's role does
              not include SETTINGS.NOTIFICATIONS. This keeps the header
              consistent with the sidebar (no page → no way in). */}
          {canSeeNotifications && (
          <div className="relative" ref={notificationsRef}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative text-muted-foreground hover:text-foreground"
              data-testid="notification-bell"
            >
              <Bell size={18} />
              {totalNotifications > 0 && (
                <span className="notification-badge">
                  {totalNotifications > 9 ? '9+' : totalNotifications}
                </span>
              )}
            </Button>
            
            {/* Notification Dropdown */}
            {showNotifications && (
              <div 
                className="absolute right-0 top-full mt-2 w-96 bg-popover rounded-xl shadow-elevation-4 border border-border z-50 max-h-[500px] overflow-hidden animate-slide-down"
                data-testid="notification-dropdown"
              >
                <div className="p-4 border-b border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <Bell size={16} />
                      Notifications
                      {notifFilterActive && (
                        <span className="text-xs font-normal text-muted-foreground">
                          ({historyLoading ? 'loading…' : `${historyNotifications.length} in window`})
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-1">
                      {/* Sound Toggle */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={toggleSound}
                        title={soundEnabled ? 'Mute notification sounds' : 'Enable notification sounds'}
                      >
                        {soundEnabled ? <Volume2 size={14} className="text-green-500" /> : <VolumeX size={14} className="text-muted-foreground" />}
                      </Button>
                      {/* Test Sound Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => testSound('comment')}
                        title="Test notification sound"
                      >
                        <Bell size={14} className="text-muted-foreground" />
                      </Button>
                      {unreadCount > 0 && (
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={handleMarkAllRead}>
                          Mark all read
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Date window — browses history beyond the live feed.
                      When either date is set we fetch up to 500 rows in the
                      window so the user can scroll back through past
                      notifications. Cleared via the Clear link. */}
                  <div className="flex items-center gap-1.5 text-xs flex-wrap">
                    <span className="text-muted-foreground">From</span>
                    <input
                      type="date"
                      value={notifFrom}
                      onChange={(e) => setNotifFrom(e.target.value)}
                      className="h-7 px-2 rounded border border-border bg-background text-foreground text-xs"
                      title="Filter notifications from this date"
                    />
                    <span className="text-muted-foreground">to</span>
                    <input
                      type="date"
                      value={notifTo}
                      onChange={(e) => setNotifTo(e.target.value)}
                      className="h-7 px-2 rounded border border-border bg-background text-foreground text-xs"
                      title="Filter notifications up to this date"
                    />
                    {notifFilterActive && (
                      <button
                        type="button"
                        onClick={() => { setNotifFrom(''); setNotifTo(''); }}
                        className="text-xs underline text-muted-foreground hover:text-foreground"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {/* Pending Access Requests */}
                  {isAdmin && pendingUsers.length > 0 && (
                    <div className="border-b border-border">
                      <div className="px-4 py-2 bg-amber-500/10">
                        <p className="text-xs font-medium text-amber-500 flex items-center gap-1">
                          <UserPlus size={12} />
                          Access Requests ({pendingUsers.length})
                        </p>
                      </div>
                      {pendingUsers.map((pendingUser) => (
                        <div key={pendingUser.id} className="p-4 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                          <div className="flex items-start gap-3">
                            <Avatar className="w-10 h-10 bg-amber-500/10 text-amber-500">
                              <AvatarFallback>
                                {pendingUser.full_name?.[0]?.toUpperCase() || pendingUser.email?.[0]?.toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-foreground text-sm truncate">
                                {pendingUser.full_name || 'Unknown User'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">{pendingUser.email}</p>
                              
                              <div className="mt-2">
                                <Select 
                                  value={selectedRoles[pendingUser.id] || 'sales_rep'} 
                                  onValueChange={(value) => setSelectedRoles(prev => ({...prev, [pendingUser.id]: value}))}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder="Select role" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="sales_manager">Sales Manager</SelectItem>
                                    <SelectItem value="sales_rep">Sales Agent</SelectItem>
                                    <SelectItem value="viewer">Viewer</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs bg-success hover:bg-success/90"
                                  onClick={() => handleApproveUser(pendingUser.id)}
                                  disabled={loadingPending}
                                >
                                  <Check size={12} className="mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
                                  onClick={() => handleRejectUser(pendingUser.id)}
                                  disabled={loadingPending}
                                >
                                  <X size={12} className="mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* General Notifications */}
                  {visibleNotifications.length === 0 && pendingUsers.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell size={32} className="mx-auto mb-2 text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground">
                        {notifFilterActive
                          ? (historyLoading ? 'Loading history…' : 'No notifications in this date window')
                          : 'No notifications'}
                      </p>
                    </div>
                  ) : (
                    visibleNotifications.map((notification) => {
                      let Icon = Bell;
                      let iconClass = 'bg-muted text-muted-foreground';
                      
                      if (notification.type === 'contact_created' || notification.type === 'owner_assigned') {
                        Icon = UserPlus;
                        iconClass = 'bg-success/10 text-success';
                      } else if (notification.type === 'owner_removed') {
                        Icon = UserPlus;
                        iconClass = 'bg-warning/10 text-warning';
                      } else if (notification.type === 'task_due' || notification.type === 'task_assigned') {
                        Icon = CheckSquare;
                        iconClass = 'bg-info/10 text-info';
                      } else if (notification.type === 'meeting_reminder' || notification.type === 'meeting_invited') {
                        Icon = Calendar;
                        iconClass = 'bg-accent/10 text-accent';
                      } else if (notification.type === 'email_received') {
                        Icon = Mail;
                        iconClass = 'bg-violet-500/10 text-violet-500';
                      } else if (notification.type === 'whatsapp_received') {
                        Icon = MessageSquare;
                        iconClass = 'bg-success/10 text-success';
                      }
                      
                      return (
                        <div 
                          key={notification.id} 
                          className={`p-4 border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer transition-colors ${!notification.is_read ? 'bg-accent/5' : ''}`}
                          onClick={() => {
                            handleMarkAsRead(notification.id);
                            const t = notification.related_type;
                            const role = (user?.role || '').toLowerCase();
                            if (t === 'contact' && notification.related_id) {
                              navigate(`/contacts/${notification.related_id}`);
                              setShowNotifications(false);
                            } else if (t === 'ceo_comment' || t === 'brief') {
                              // CEO ↔ HOD thread (the CEO Comments tab on the HOD dashboard).
                              navigate('/hod-dashboard?tab=ceo-comments');
                              setShowNotifications(false);
                            } else if (t === 'feedback' || t === 'voice_message') {
                              // Direct feedback addressed to this user. HODs view it
                              // in the CEO Comments tab; agents see it in their own
                              // Feedback from Management card on /my-dashboard.
                              const dest = role.startsWith('hod') || role === 'ceo'
                                ? '/hod-dashboard?tab=ceo-comments'
                                : '/my-dashboard#feedback';
                              navigate(dest);
                              setShowNotifications(false);
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-full ${iconClass} flex items-center justify-center flex-shrink-0`}>
                              <Icon size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''} text-foreground`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
                              <p className="text-xs text-muted-foreground/70 mt-1">
                                {new Date(notification.created_at).toLocaleString('en-US', { 
                                  timeZone: 'Asia/Dubai',
                                  month: 'short', 
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-2" />
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer link to the full Notification Settings page —
                    gives the user a one-click path to the Sounds tab
                    (per-category tones + custom uploads) and other
                    notification preferences. */}
                <div className="p-2 border-t border-border bg-muted/30">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/settings/notifications');
                      setShowNotifications(false);
                    }}
                    className="w-full text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded py-1.5 flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Settings size={12} />
                    Notification settings & sounds
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Language Selector */}
          <LanguageSelector variant="compact" />
          
          {/* User Profile */}
          <div 
            className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 rounded-xl px-3 py-2 transition-colors ml-1"
            onClick={() => navigate('/profile')}
            data-testid="user-profile-link"
          >
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-foreground">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground">{user?.company || 'No company'}</p>
            </div>
            <Avatar className="w-9 h-9 border-2 border-border" data-testid="user-avatar">
              <AvatarFallback className="bg-accent/10 text-accent font-semibold text-sm">
                {getInitials(user?.full_name || 'U')}
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>
    </header>
  );
};

const AppShell = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved === 'true';
  });
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebar_collapsed', String(newState));
  };
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  // Close mobile menu when clicking outside
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="App">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}
      <Sidebar 
        collapsed={sidebarCollapsed} 
        onToggleCollapse={toggleSidebar}
        mobileOpen={mobileMenuOpen}
        onMobileClose={closeMobileMenu}
      />
      <div className={`main-content ${sidebarCollapsed ? 'ml-16' : ''}`}>
        <Topbar onMobileMenuToggle={toggleMobileMenu} />
        <main className="page-container animate-fade-in">{children}</main>
      </div>
      {/* AI Chat Widget - Floating */}
      <AIChatWidget />
      {/* Centred follow-up reminder popup with notification sound */}
      <FollowupReminderOverlay />
    </div>
  );
};

export default AppShell;
